import { collection, doc, getDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import type {
  AddedPosition,
  ILogger,
  IRuntimeConfigRepository,
  RepositoryError,
  RuntimeConfigPatch,
  TestMailReceipt,
} from '@application/ports';
import {
  DEFAULT_CATALOGS_PLACEHOLDER_NOTE,
  DEFAULT_IDENTITY,
  DEFAULT_REJECTION_REASONS,
  DEFAULT_POSITIONS,
  type Position,
  DEFAULT_RULES,
  DEFAULT_SENIORITY_BRACKETS,
  EMAIL_EVENTS,
  type CatalogsConfig,
  type EmailEvent,
  type EmailTemplate,
  type IdentityConfig,
  type RejectionReason,
  type RulesConfig,
  type RuntimeConfig,
} from '@domain/entities/RuntimeConfig';
import { DEFAULT_INCAPACITY_KINDS, type IncapacityKind } from '@domain/entities/Incapacity';
import { DEFAULT_PERMISSION_KINDS, type PermissionKind } from '@domain/entities/PermissionRequest';
import { normalizeMatrix } from '@domain/policies/RbacPolicy';
import {
  DEFAULT_FEATURES,
  FEATURES,
  PERMISSIONS,
  REJECTION_SCOPES,
  ROLES,
  type FeatureKey,
  type Permission,
  type RejectionScope,
  type Role,
} from '@domain/vocabulary';
import { AREA_FEATURES, type AreaCatalog } from '@domain/entities/AreaCatalog';
import { clampExpiryMonths } from '@domain/policies/VacationPeriodPolicy';
import { CONFIG_DOCS, COLLECTIONS, firebase } from '../firebase/client';
import { translateCallableError, translateFirestoreError } from './support';

/**
 * The OTA configuration adapter — the piece that makes "change behaviour without
 * a redeploy" real.
 *
 * `subscribe` attaches a live listener to the whole `config` collection. It is one
 * listener for six small singleton documents, so a superuser toggling a feature
 * flag propagates to every open session within a round trip, with no polling and
 * no reload.
 *
 * Every field is decoded with a fallback and every unrecognised value is dropped
 * rather than failing the parse. That is deliberate to the point of being the whole
 * design: this document set is edited by humans through a UI, read by clients on
 * several different builds at once, and consulted on literally every screen. A
 * decode that could throw here would be able to take the entire app down from a
 * text field.
 */

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const str = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;

const num = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const int = (value: unknown, fallback: number): number => Math.trunc(num(value, fallback));

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const list = (value: unknown): readonly unknown[] => (Array.isArray(value) ? value : []);

// ---------------------------------------------------------------------------
// Per-document decoding
// ---------------------------------------------------------------------------

const decodeIdentity = (raw: unknown): IdentityConfig => {
  const data = asRecord(raw);
  const domains = list(data['allowedDomains'])
    .filter((d): d is string => typeof d === 'string')
    .map((d) => d.trim().replace(/^@/, '').toLowerCase())
    .filter((d) => d.length > 0);

  return {
    commercialName: str(data['commercialName'], DEFAULT_IDENTITY.commercialName),
    // An empty list would let nobody in rather than everybody, so the fallback is
    // the configured default domain — never "any domain".
    allowedDomains: domains.length > 0 ? domains : DEFAULT_IDENTITY.allowedDomains,
    hrEmail: str(data['hrEmail'], DEFAULT_IDENTITY.hrEmail),
    // Falls back to blank, not to a constructed address: an installation whose
    // sender nobody has written down must read as unknown, because the screen that
    // shows it is the one people trust to tell them where mail comes from.
    mailFrom: str(data['mailFrom'], DEFAULT_IDENTITY.mailFrom),
    country: str(data['country'], DEFAULT_IDENTITY.country),
    logoPath: typeof data['logoPath'] === 'string' ? data['logoPath'] : null,
  };
};

const decodeRules = (raw: unknown): RulesConfig => {
  const data = asRecord(raw);
  const seniorityRaw = asRecord(data['seniority']);

  const brackets = list(seniorityRaw['brackets'])
    .map((entry) => {
      const bracket = asRecord(entry);
      return { fromYear: int(bracket['fromYear'], 0), days: int(bracket['days'], 0) };
    })
    .filter((b) => b.fromYear > 0 && b.days >= 0)
    .sort((a, b) => a.fromYear - b.fromYear);

  return {
    legalMinimumDays: Math.max(0, int(data['legalMinimumDays'], DEFAULT_RULES.legalMinimumDays)),
    noticeDays: Math.max(0, int(data['noticeDays'], DEFAULT_RULES.noticeDays)),
    minimumRequestDays: Math.max(1, int(data['minimumRequestDays'], DEFAULT_RULES.minimumRequestDays)),
    /* Acotado igual que en el servidor (`clampExpiryMonths`): lo que esta pantalla
       enseña tiene que ser lo que el servidor cobra. */
    expiryMonths: clampExpiryMonths(int(data['expiryMonths'], DEFAULT_RULES.expiryMonths)),
    /* Acotado, no saneado: un `0` guardado por error apagaría el cierre por
       inactividad y nada en pantalla lo delataría. Cinco minutos abajo, ocho
       horas arriba. */
    idleMinutes: Math.min(
      480,
      Math.max(5, int(data['idleMinutes'], DEFAULT_RULES.idleMinutes)),
    ),
    riskThresholdDays: Math.max(0, int(data['riskThresholdDays'], DEFAULT_RULES.riskThresholdDays)),
    // Zero is a legitimate cap ("nobody may be out"), so this is clamped at zero
    // rather than falling back — but a *missing* value must not read as zero,
    // which is why the default is applied before the clamp.
    defaultMaxConcurrentOut: Math.max(
      0,
      int(data['defaultMaxConcurrentOut'], DEFAULT_RULES.defaultMaxConcurrentOut),
    ),
    /* Ausentes en toda base anterior a Compensation: caen al piso de ley. */
    aguinaldoDays: Math.max(0, int(data['aguinaldoDays'], DEFAULT_RULES.aguinaldoDays)),
    primaVacacionalRate: Math.max(
      0,
      num(data['primaVacacionalRate'], DEFAULT_RULES.primaVacacionalRate),
    ),
    minimumWageCents: Math.max(
      0,
      int(data['minimumWageCents'], DEFAULT_RULES.minimumWageCents),
    ),
    seniority: {
      enabled: bool(seniorityRaw['enabled'], DEFAULT_RULES.seniority.enabled),
      fixedDays: Math.max(0, int(seniorityRaw['fixedDays'], DEFAULT_RULES.seniority.fixedDays)),
      additionalDays: Math.max(
        0,
        int(seniorityRaw['additionalDays'], DEFAULT_RULES.seniority.additionalDays),
      ),
      brackets: brackets.length > 0 ? brackets : DEFAULT_SENIORITY_BRACKETS,
    },
  };
};

const decodeIncapacityKinds = (raw: unknown): readonly IncapacityKind[] => {
  const kinds = list(raw)
    .map((entry) => {
      const kind = asRecord(entry);
      return {
        id: str(kind['id'], ''),
        note: str(kind['note'], ''),
        subsidy: str(kind['subsidy'], ''),
        /* Certificado por el IMSS salvo que diga lo contrario: un catálogo ya
           sembrado no trae el campo y sus cuatro tipos sí lo están. */
        imssCertified: bool(kind['imssCertified'], true),
        enabled: bool(kind['enabled'], true),
      };
    })
    .filter((k) => k.id.length > 0);
  return kinds.length > 0 ? kinds : DEFAULT_INCAPACITY_KINDS;
};

const decodePermissionKinds = (raw: unknown): readonly PermissionKind[] => {
  const kinds = list(raw)
    .map((entry) => {
      const kind = asRecord(entry);
      return {
        id: str(kind['id'], ''),
        note: str(kind['note'], ''),
        icon: str(kind['icon'], '·'),
        measuredInHours: bool(kind['measuredInHours'], false),
        /* Apagado por omisión: un catálogo ya sembrado no lleva el campo, y
           suponer que un permiso de horas no vuelve sería inventarle al portal
           una ausencia de media tarde que nadie pidió. Se enciende a mano en
           Configuración global › Catálogos. */
        openEnded: bool(kind['openEnded'], false),
        /* Apagada por omisión, y eso conserva lo de hoy: el asistente ya impedía
           fechas pasadas para todos los tipos, así que un catálogo sembrado sin el
           campo se comporta igual que antes. Paternidad y fallecimiento se
           encienden a mano en Configuración global › Catálogos. */
        retroactive: bool(kind['retroactive'], false),
        withPay: bool(kind['withPay'], true),
        enabled: bool(kind['enabled'], true),
      };
    })
    .filter((k) => k.id.length > 0);
  return kinds.length > 0 ? kinds : DEFAULT_PERMISSION_KINDS;
};

const decodeRejectionReasons = (raw: unknown): readonly RejectionReason[] => {
  const reasons = list(raw)
    .map((entry) => {
      const reason = asRecord(entry);
      const scope = str(reason['scope'], '');
      return {
        id: str(reason['id'], ''),
        text: str(reason['text'], ''),
        // An unrecognised scope defaults to `vacaciones` rather than being dropped:
        // losing a reason silently would make it un-selectable with no explanation.
        scope: ((REJECTION_SCOPES as readonly string[]).includes(scope)
          ? scope
          : 'vacaciones') as RejectionScope,
        enabled: bool(reason['enabled'], true),
      };
    })
    .filter((r) => r.id.length > 0 && r.text.length > 0);
  return reasons.length > 0 ? reasons : DEFAULT_REJECTION_REASONS;
};

/**
 * Los puestos. Ausentes en toda base anterior a este catálogo, que son todas: cae
 * a los de fábrica igual que hacen sus vecinos, en vez de dejar la lista vacía y
 * un selector sin opciones.
 */
const decodePositions = (raw: unknown): readonly Position[] => {
  const list = Array.isArray(raw) ? raw : [];
  const positions = list
    .map((entry) => {
      const item = asRecord(entry);
      return {
        id: str(item['id'], '').trim(),
        title: str(item['title'], '').trim(),
        enabled: item['enabled'] !== false,
      };
    })
    .filter((p) => p.id.length > 0 && p.title.length > 0);
  return positions.length > 0 ? positions : DEFAULT_POSITIONS;
};

const decodeCatalogs = (raw: unknown): CatalogsConfig => {
  const data = asRecord(raw);
  return {
    incapacityKinds: decodeIncapacityKinds(data['incapacityKinds']),
    permissionKinds: decodePermissionKinds(data['permissionKinds']),
    rejectionReasons: decodeRejectionReasons(data['rejectionReasons']),
    positions: decodePositions(data['positions']),
  };
};

/**
 * Feature flags. Any key this build does not know is ignored, and any key it knows
 * that the document omits falls back to its default — so a newer backend adding a
 * flag does not switch anything off here, and an older document does not hide a
 * module this build ships.
 */
const decodeFeatures = (raw: unknown): Readonly<Record<FeatureKey, boolean>> => {
  const data = asRecord(raw);
  const out: Record<string, boolean> = {};
  for (const key of FEATURES) {
    out[key] = bool(data[key], DEFAULT_FEATURES[key]);
  }
  return out as Readonly<Record<FeatureKey, boolean>>;
};

const decodeRolePermissions = (raw: unknown): Readonly<Record<Role, readonly Permission[]>> => {
  const data = asRecord(raw);
  const asStringLists: Record<string, readonly string[]> = {};
  for (const role of ROLES) {
    const stored = data[role];
    if (Array.isArray(stored)) {
      asStringLists[role] = stored.filter((p): p is string => typeof p === 'string');
    }
  }
  // `normalizeMatrix` fills in missing roles, drops unknown permissions and
  // re-adds the protected ones. Without it a role absent from the document would
  // make every `can()` false and present the user with an empty application.
  return normalizeMatrix(asStringLists, PERMISSIONS, ROLES);
};

const decodeEmailTemplates = (raw: unknown): Readonly<Record<EmailEvent, EmailTemplate>> => {
  const data = asRecord(raw);
  const out: Partial<Record<EmailEvent, EmailTemplate>> = {};

  for (const event of EMAIL_EVENTS) {
    const stored = asRecord(data[event]);
    out[event] = {
      event,
      enabled: bool(stored['enabled'], true),
      subject: str(stored['subject'], ''),
      body: str(stored['body'], ''),
      ctaLabel: str(stored['ctaLabel'], 'Abrir el portal'),
      audience: str(stored['audience'], ''),
      timing: str(stored['timing'], ''),
      glyph: str(stored['glyph'], '·'),
      color: str(stored['color'], '#4A6FA5'),
    };
  }

  return out as Readonly<Record<EmailEvent, EmailTemplate>>;
};

const decodeSystemRuntime = (
  raw: unknown,
): { minClientVersion: string; maintenanceBanner: string | null } => {
  const data = asRecord(raw);
  return {
    minClientVersion: str(data['minClientVersion'], '0.0.0'),
    maintenanceBanner:
      typeof data['maintenanceBanner'] === 'string' && data['maintenanceBanner'].trim().length > 0
        ? data['maintenanceBanner'].trim()
        : null,
  };
};

/**
 * Lo que un área cambia sobre los dos catálogos.
 *
 * Se guarda por id y se mezcla campo a campo sobre el global; ver `resolveKinds`.
 * Aquí sólo se limpia la forma: lo que no sea objeto con `id` no entra, porque
 * esto lo lee el asistente de cualquiera y un documento torcido no puede dejar a
 * nadie sin poder pedir un permiso.
 */
const decodeAreaEntries = (raw: unknown): readonly Record<string, unknown>[] => {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((entry) => asRecord(entry))
    .filter((entry) => typeof entry['id'] === 'string' && entry['id'].length > 0);
};

/**
 * Los módulos que el área encendió o apagó por su cuenta.
 *
 * **Una llave ausente se queda ausente**, y no se rellena con un valor por
 * omisión como hace `decodeFeatures`: aquí la ausencia es el tercer estado —
 * «hereda»— y escribir `false` donde no había nada convertiría a todas las áreas
 * en áreas que apagaron el módulo a mano. Lo que no sea booleano se descarta.
 */
const decodeAreaFeatures = (raw: unknown): AreaCatalog['features'] => {
  const data = asRecord(raw);
  const out: Record<string, boolean> = {};
  for (const key of AREA_FEATURES) {
    if (typeof data[key] === 'boolean') out[key] = data[key];
  }
  return out;
};

const decodeAreaCatalogs = (
  documents: ReadonlyMap<string, unknown>,
): Readonly<Record<string, AreaCatalog>> => {
  const out: Record<string, AreaCatalog> = {};
  for (const [areaId, raw] of documents) {
    const data = asRecord(raw);
    out[areaId] = {
      areaId,
      incapacityKinds: decodeAreaEntries(data['incapacityKinds']),
      permissionKinds: decodeAreaEntries(data['permissionKinds']),
      features: decodeAreaFeatures(data['features']),
    };
  }
  return out;
};

/** Assembles the aggregate from the raw `config/*` documents plus `system/runtime`. */
const assembleConfig = (
  documents: ReadonlyMap<string, unknown>,
  runtime: unknown,
  areaCatalogs: ReadonlyMap<string, unknown> = new Map(),
): RuntimeConfig => {
  const system = decodeSystemRuntime(runtime);
  return {
    identity: decodeIdentity(documents.get(CONFIG_DOCS.identity)),
    rules: decodeRules(documents.get(CONFIG_DOCS.rules)),
    catalogs: decodeCatalogs(documents.get(CONFIG_DOCS.catalogs)),
    features: decodeFeatures(documents.get(CONFIG_DOCS.featureFlags)),
    rolePermissions: decodeRolePermissions(documents.get(CONFIG_DOCS.rolePermissions)),
    emailTemplates: decodeEmailTemplates(documents.get(CONFIG_DOCS.emailTemplates)),
    areaCatalogs: decodeAreaCatalogs(areaCatalogs),
    minClientVersion: system.minClientVersion,
    maintenanceBanner: system.maintenanceBanner,
  };
};

/**
 * The configuration a client falls back to when the documents cannot be read at
 * all — an offline first load, say.
 *
 * Deliberately the factory defaults with **every feature on**: an unreachable
 * configuration document must not present the user with an application that
 * appears to have lost half its modules. The security floor is unaffected, because
 * it lives in the rules and not here.
 */
export const fallbackRuntimeConfig = (): RuntimeConfig =>
  assembleConfig(new Map(), undefined);

export class FirestoreRuntimeConfigRepository implements IRuntimeConfigRepository {
  constructor(private readonly logger: ILogger) {}

  async load(): Promise<Result<RuntimeConfig, RepositoryError>> {
    try {
      const { db } = firebase();
      const [configSnapshot, runtimeSnapshot, areaSnapshot] = await Promise.all([
        getDocs(collection(db, COLLECTIONS.config)),
        getDoc(doc(db, COLLECTIONS.system, 'runtime')),
        getDocs(collection(db, COLLECTIONS.areaCatalogs)),
      ]);

      const documents = new Map<string, unknown>(
        configSnapshot.docs.map((d) => [d.id, d.data()]),
      );
      const areaCatalogs = new Map<string, unknown>(areaSnapshot.docs.map((d) => [d.id, d.data()]));
      return ok(assembleConfig(documents, runtimeSnapshot.data(), areaCatalogs));
    } catch (cause) {
      return err(translateFirestoreError(cause, 'runtime config'));
    }
  }

  /**
   * Live subscription.
   *
   * Two listeners — the `config` collection and `system/runtime` — and the callback
   * fires only once both have reported at least once, so a consumer never sees a
   * half-assembled configuration with default rules and real feature flags.
   */
  subscribe(
    onChange: (config: RuntimeConfig, fromCache: boolean) => void,
    onError: (error: RepositoryError) => void,
  ): () => void {
    const { db } = firebase();

    const documents = new Map<string, unknown>();
    const areaCatalogs = new Map<string, unknown>();
    let runtime: unknown;
    let configSeen = false;
    let runtimeSeen = false;
    /*
     * El tercer canal no bloquea la entrega.
     *
     * Es lo que cada área cambia sobre incapacidades y permisos, y un área sin
     * documento hereda el catálogo global — que es el estado de las ocho hasta
     * que alguien edite. Esperarlo para emitir dejaría la aplicación entera en
     * los valores de fábrica por una colección que puede estar vacía con toda
     * legitimidad.
     */
    /*
     * Si lo último que entregó el canal salió del caché y no del servidor.
     *
     * Firestore emite el estado local antes de hablar con nadie, así que el
     * primer snapshot casi siempre es de caché incluso en una red sana —y llega
     * uno del servidor detrás—. Donde importa es en la red que bloquea el canal:
     * ahí el de caché es el único que llega, y **un caché vacío es un snapshot
     * de cero documentos**, indistinguible de una configuración que no existe.
     * Quien decide qué hacer con eso es el proveedor; aquí sólo se dice.
     */
    let configFromCache = true;

    const emitWhenReady = (): void => {
      if (!configSeen || !runtimeSeen) return;
      onChange(assembleConfig(documents, runtime, areaCatalogs), configFromCache);
    };

    const unsubscribeConfig = onSnapshot(
      collection(db, COLLECTIONS.config),
      (snapshot) => {
        for (const change of snapshot.docChanges()) {
          if (change.type === 'removed') documents.delete(change.doc.id);
          else documents.set(change.doc.id, change.doc.data());
        }
        configSeen = true;
        configFromCache = snapshot.metadata.fromCache;
        emitWhenReady();
      },
      (cause) => {
        this.logger.error('config listener failed', { cause: cause.message });
        onError(translateFirestoreError(cause, 'config listener'));
      },
    );

    const unsubscribeAreas = onSnapshot(
      collection(db, COLLECTIONS.areaCatalogs),
      (snapshot) => {
        for (const change of snapshot.docChanges()) {
          if (change.type === 'removed') areaCatalogs.delete(change.doc.id);
          else areaCatalogs.set(change.doc.id, change.doc.data());
        }
        emitWhenReady();
      },
      (cause) => {
        /* Degrada, no rompe: sin esto las ocho áreas ven el catálogo global, que
           es exactamente lo que veían antes de que esto existiera. */
        this.logger.error('area catalog listener failed', { cause: cause.message });
      },
    );

    const unsubscribeRuntime = onSnapshot(
      doc(db, COLLECTIONS.system, 'runtime'),
      (snapshot) => {
        runtime = snapshot.data();
        runtimeSeen = true;
        emitWhenReady();
      },
      (cause) => {
        // `system/runtime` carries only the version gate and the banner. Losing it
        // must not block the app, so this degrades rather than erroring: the
        // configuration is emitted with the gate at its permissive default.
        this.logger.warn('system/runtime listener failed; continuing without it', {
          cause: cause.message,
        });
        runtimeSeen = true;
        emitWhenReady();
      },
    );

    return () => {
      unsubscribeConfig();
      unsubscribeAreas();
      unsubscribeRuntime();
    };
  }

  /**
   * Writes configuration through a callable rather than directly.
   *
   * The security rules deny every client write to `config/*`. The callable checks
   * the superuser claim, validates the payload, and — the reason it exists at all —
   * writes an audit entry alongside the change. A configuration edit that changes
   * everyone's permissions must leave a trace of who made it.
   */
  async save(patch: RuntimeConfigPatch): Promise<Result<void, RepositoryError>> {
    try {
      const { functions } = firebase();
      const call = httpsCallable<RuntimeConfigPatch, { ok: boolean }>(
        functions,
        'updateRuntimeConfig',
      );
      await call(patch);
      return ok(undefined);
    } catch (cause) {
      return err(translateCallableError(cause, 'updateRuntimeConfig'));
    }
  }

  /**
   * Da de alta un puesto. El servidor decide si ya existía —ver
   * `addCatalogPosition`— y devuelve el que quedó, que es lo que se enseña.
   */
  async addPosition(title: string): Promise<Result<AddedPosition, RepositoryError>> {
    try {
      const { functions } = firebase();
      const call = httpsCallable<
        { title: string },
        { id: string; title: string; created: boolean }
      >(functions, 'addCatalogPosition');
      const { data } = await call({ title });
      return ok({
        position: { id: data.id, title: data.title, enabled: true },
        created: data.created,
      });
    } catch (cause) {
      return err(translateCallableError(cause, 'addCatalogPosition'));
    }
  }

  /**
   * Queues one template as a rehearsal.
   *
   * The callable picks the recipient — the caller's own verified address — and this
   * returns it rather than assuming, so the screen can print the inbox to check
   * instead of a sentence somebody has to trust.
   */
  async sendTest(
    event: EmailEvent,
    areaId: string,
  ): Promise<Result<TestMailReceipt, RepositoryError>> {
    try {
      const { functions } = firebase();
      const call = httpsCallable<
        { event: EmailEvent; areaId: string },
        { queued: boolean; recipient: string; subject: string }
      >(functions, 'sendTestEmail');
      const response = await call({ event, areaId });
      return ok({ recipient: response.data.recipient, subject: response.data.subject });
    } catch (cause) {
      return err(translateCallableError(cause, 'sendTestEmail'));
    }
  }
}

/** Referenced by the seeder so the note stays in one place. */
export const CATALOG_SEED_NOTE = DEFAULT_CATALOGS_PLACEHOLDER_NOTE;
